import { useState, useEffect } from 'react';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useEmbeddedPageTracking } from '@atlassian/jira-issue-create-confluence-content/src/controllers/use-embedded-page-tracking/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import { fetchUserAddProductPermissions } from '../services/user-product-permission.tsx';

const USER_ADD_PRODUCT_PERMISSION = 'userAddProductPermission';
type Props = {
	skip?: boolean;
};

export const useUserHasAddProductPermissions = ({ skip = false }: Props) => {
	const cloudId = useCloudId();
	const isSiteAdmin = useIsSiteAdmin();

	const [status, setStatus] = useState<{
		canProvisionConfluence: boolean;
		fetchStatus: 'pending' | 'resolved' | 'failed';
	}>({ canProvisionConfluence: false, fetchStatus: 'pending' });
	const fireAnalyticsEvent = useEmbeddedPageTracking();

	useEffect(() => {
		if (skip) {
			return;
		}

		if (isSiteAdmin) {
			// Shortcut - Site admins have add-product permissions
			setStatus({ canProvisionConfluence: true, fetchStatus: 'resolved' });
			return;
		}

		const getAddProductPermissions = async () => {
			try {
				const response = await fetchUserAddProductPermissions(cloudId);
				setStatus({ canProvisionConfluence: response?.permitted, fetchStatus: 'resolved' });
				fireAnalyticsEvent({
					action: 'success',
					eventName: USER_ADD_PRODUCT_PERMISSION,
				});
			} catch (error: unknown) {
				setStatus({ canProvisionConfluence: false, fetchStatus: 'failed' });
				fireErrorAnalytics({
					meta: {
						id: USER_ADD_PRODUCT_PERMISSION,
						packageName: 'jiraIssueViewBase',
						teamName: 'bento',
					},
					error: new Error(`Failed to get ${USER_ADD_PRODUCT_PERMISSION} status: ${error}`),
				});
			}
		};
		getAddProductPermissions();
	}, [cloudId, fireAnalyticsEvent, isSiteAdmin, skip]);

	return status;
};
